import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { add, orderBy, set, isEqual } from 'lodash';
import CapsuleTab from './CapsuleTab';
import Button from '../Button';
import 'react-calendar/dist/Calendar.css';
import { format, addDays, endOfDay } from 'date-fns/fp';
import Icon from '../Icon';
import Drawer from '../Drawer';
import { differenceInDays } from 'date-fns';
import Spinner from '../Spinner';
import DrawerAccordion from '../DrawerAccordion';
import { ValidateEmail, applyHtmlLang } from '../../helper';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

import { QuickPriceCalculatorLister, QuickPriceCalculatorTypeLister } from '../../semshared/pricelist/quickprice_lister';
import { BaseCapsule, CapsuleAd, CapsuleTabContainer, ClickOverlay } from './CapsuleComponents';
import CalendarSection from './sections/CalendarSection';
import GuestSection from './sections/GuestsSection';
import MeetingRoomsSectionPopup from './sections/MeetingRoomsSectionPopup';
import MeetingRoomsSectionDrawer from './sections/MeetingRoomsSectionDrawer';
import RoomsSection from './sections/RoomsSection';
import { position } from 'polished';
import { Availability, AvailabilityType } from '../../semshared/availability/availability';
import { context, trace } from '@opentelemetry/api';

// import Autocomplete, { usePlacesWidget } from "react-google-autocomplete";
import { Addon, getFormState, getInitialFormState, IFormState, isInitalFormState, updateFormState, clearFormStateIfReset } from '../../semshared/utils/lister';
import PackageSection from './sections/ServiceTypeSection';
import ServiceTypeSection from './sections/ServiceTypeSection';
import GooglePlacesAutocomplete from '../GooglePlacesAutocomplete';

// --- Confirmation Dialog Component ---
interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  description: string;
}

/**
 * Lightweight confirmation dialog for booking update decisions.
 * @param props - Visibility and callback handlers for confirm/cancel actions.
 * @returns Modal overlay when `open` is true, otherwise null.
 */
const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({ open, onClose, onConfirm, onCancel, title, description }) => {
  const { t } = useTranslation();

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000 // Ensure it's above other elements
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        maxWidth: '400px',
        textAlign: 'center',
        fontFamily: 'sans-serif'
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '10px', fontSize: '1.2em', color: '#333' }}>{title}</h2>
        <p style={{ marginBottom: '20px', color: '#555' }}>{description}</p>
        <div style={{ display: 'flex', justifyContent: 'space-around' }}>
          <Button
            color={'#AC2A6E'}
            filled={false}
            onClick={onCancel}
            style={{ border: '1px solid #AC2A6E', padding: '8px 16px' }}
          >
            {t('widget:confirmation.cancel')}
          </Button>
          <Button
            color={'#AC2A6E'}
            filled
            onClick={onConfirm}
            style={{ padding: '8px 16px' }}
          >
            {t('widget:confirmation.confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
};
// --- End Confirmation Dialog Component ---

interface CapsuleProps {
  label: string;
  shadowRoot: ShadowRoot | null;
  formState: string | null;
  platformUrl: string;
  hotelView: string | null;
  autoUpdate?: boolean;
  language?: string | null;
  navDebug?: boolean;
}

type ValuePiece = Date | null;

/**
 * Main booking capsule that coordinates form state, sections, and host integration events.
 * @param props - Runtime widget configuration and host mount context.
 * @returns Full widget capsule with popup and drawer navigation variants.
 */
const Capsule = ({
  label,
  shadowRoot,
  formState: s,
  platformUrl,
  hotelView,
  autoUpdate = false,
  language = null,
  navDebug = false,
}: CapsuleProps) => {
  const { t } = useTranslation();

  const isMobileSize = () => {
    return document.documentElement.clientWidth < 1200;
  };

  const isHotelView = hotelView === 'true' || hotelView === '1';

  // Mobile: collapsed view with just a button
  const [expanded, setExpanded] = React.useState(!isMobileSize());

  const [totalPriceNet, setTotalPriceNet] = React.useState<string | null>(null);
  const [totalPriceGross, setTotalPriceGross] = React.useState<string | null>(null);

  const [popupShow, setPopupShow] = React.useState<string | null>(null);
  const [drawerShow, setDrawerShow] = React.useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [termsAccepted, setTermsAccepted] = React.useState(false);
  const [reservationStatus, setReservationStatus] = React.useState<string>('READY');
  const [emailVerificationCode, setEmailVerificationCode] = React.useState('');
  const [emailVerificationCodeInvalid, setEmailVerificationCodeInvalid] = React.useState(false);

  const [formState, setFormState] = React.useState<IFormState | null>(null);
  const [isInitFormState, setIsInitFormState] = useState<Boolean>(true);
  const [buttonEnabled, setButtonEnabled] = useState(false);


  // Ref to track initial load
  const isInitialLoad = useRef(true);
  // State to hold the state to revert to if 'Abbrechen' is clicked
  const [stateToRevertTo, setStateToRevertTo] = useState<Partial<IFormState> | null>(null);
  // State to hold the pending state update
  const [pendingFormState, setPendingFormState] = useState<IFormState | null>(null);
  // State to track if confirmation dialog has been shown this session
  const [hasShownUpdateDialogThisSession, setHasShownUpdateDialogThisSession] = useState(false);

  // State for confirmation dialog
  const [showUpdateConfirmDialog, setShowUpdateConfirmDialog] = useState(false);

  // State to indicate if an interaction has forced advanced mode
  const [userInitiatedAdvancedMode, setUserInitiatedAdvancedMode] = React.useState(false);

  const [popupExpanded, setPopupExpanded] = React.useState(false);
  const [serviceTypes, setServiceTypes] = React.useState<any[]>([]);
  const [serviceTypesError, setServiceTypesError] = React.useState<string>('');
  const [quickPriceCalculator, setQuickPriceCalculator] = React.useState<QuickPriceCalculatorTypeLister | null>(null);
  const [availabilityChecker, setAvailabilityChecker] = React.useState<AvailabilityType | null>(null);
  const [meetingRoomsDay, setMeetingRoomsDay] = React.useState(0);
  const [errors, setErrors] = React.useState<any>({});

  const [calValue, onChange] = useState<ValuePiece | [ValuePiece, ValuePiece]>([null, null]);

  const [simpleMode, setSimpleMode] = React.useState(true);
  const drawerScrollRef = useRef<HTMLDivElement>(null);

  /*useEffect(() => {
    if (prevFormStateRef.current.meetingRooms > formState.meetingRooms) {
      setFormState({
        ...formState,
        days: formState.days.map(day => ({
          ...day,
          occupancy: day.occupancy.slice(0, formState.meetingRooms),
        })),
      });
    } else if (prevFormStateRef.current.meetingRooms < formState.meetingRooms) {
      setFormState({
        ...formState,
        days: formState.days.map(day => ({
          ...day,
          occupancy: [...day.occupancy, ...Array.from({ length: formState.meetingRooms - day.occupancy.length }, (_, i) => 'FULLDAY')],
        })),
      });
    }
  }, [formState.meetingRooms]);*/

  useEffect(() => {
    if (formState) {
      updateFormState(formState);
      isInitalFormState(formState).then((isInit) => {
        setIsInitFormState(isInit);
      });
    }
  }, [formState]);

  const [capsuleWrapperPositionLeft, setCapsuleWrapperPositionLeft] = useState<number | undefined>(undefined);
  const [capsuleWrapperPositionTop, setCapsuleWrapperPositionTop] = useState<number | undefined>(undefined);
  const [capsuleWrapperWidth, setCapsuleWrapperWidth] = useState<number | undefined>(undefined);
  const capsuleWrapperRef = React.useRef<HTMLDivElement>(null);

  const [widgetClickedOnce, setWidgetClickedOnce] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  function handleResize() {
    setCapsuleWrapperPositionLeft(capsuleWrapperRef.current?.getBoundingClientRect().left);
    setCapsuleWrapperPositionTop(
      (capsuleWrapperRef.current?.getBoundingClientRect().top || 0) +
      (capsuleWrapperRef.current?.getBoundingClientRect().height || 0) +
      window.scrollY,
    );
    setCapsuleWrapperWidth(capsuleWrapperRef.current?.getBoundingClientRect().width);
  }

  useLayoutEffect(() => {
    const handleResizeComplete = () => {
      handleResize();
      // Update expanded state based on screen size
      const mobile = isMobileSize();
      setExpanded(!mobile);
    };

    handleResizeComplete();
    window.addEventListener('resize', handleResizeComplete);
    const interval = setInterval(() => {
      handleResize();
    }, 100);

    return () => {
      window.removeEventListener('resize', handleResizeComplete);
      clearInterval(interval);
    };
  }, []);

  // Language synchronization effect
  useEffect(() => {
    // Priority 1: Use language prop from attribute (data-language)
    // Priority 2: Check URL path
    let initialLocale: string;

    if (language && (language === 'en' || language === 'de')) {
      initialLocale = language;
    } else {
      const pathname = window.location.pathname;
      initialLocale = pathname.startsWith('/en') ? 'en' : 'de';
    }

    // Set initial language if different
    if (i18n.language !== initialLocale) {
      i18n.changeLanguage(initialLocale);
    }
    applyHtmlLang(initialLocale);

    const handleLanguageChange = (event: any) => {
      const { locale } = event.detail;
      if (locale && i18n.language !== locale) {
        i18n.changeLanguage(locale);
      }
      if (locale) {
        applyHtmlLang(locale);
      }
    };

    const handleResetDialogState = () => {
      // Reset the dialog state so it can be shown again after user cancels
      setHasShownUpdateDialogThisSession(false);
    };

    const handleFormStateReset = () => {
      // When form state is reset (e.g., booking edit canceled), reset dialog state too
      setHasShownUpdateDialogThisSession(false);

      // Reload form state from localStorage to get the updated state without bookingRefCode
      getFormState().then((state) => {
        setFormState(state);
      });
    };

    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    window.addEventListener('resetWidgetDialogState', handleResetDialogState as EventListener);
    window.addEventListener('formStateReset', handleFormStateReset as EventListener);

    return () => {
      window.removeEventListener('languageChange', handleLanguageChange as EventListener);
      window.removeEventListener('resetWidgetDialogState', handleResetDialogState as EventListener);
      window.removeEventListener('formStateReset', handleFormStateReset as EventListener);
    };
  }, [language]);

  useEffect(() => {
    const handleHotelCandidatesUpdate = (event: CustomEvent) => {
      setButtonEnabled(event.detail.hasHotels);
    };
    window.addEventListener('hotelCandidatesUpdate', handleHotelCandidatesUpdate as EventListener);
    return () => {
      window.removeEventListener('hotelCandidatesUpdate', handleHotelCandidatesUpdate as EventListener);
    };
  }, []);

  useEffect(() => {
    const loadFormState = async () => {
      // Check if widget reset was triggered and clear localStorage if needed
      const pricelistServiceUrl = platformUrl || process.env.REACT_APP_PRICELIST_URL || '';
      await clearFormStateIfReset(pricelistServiceUrl);

      const state = await getFormState();
      console.log('Form state loaded2', state);

      // Check if start date is in the past
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (state.start && state.start < today) {
        // Calculate tomorrow's date
        const tomorrow = addDays(1, today);

        // Calculate the original duration between start and end
        const duration = state.end ? differenceInDays(state.end, state.start) : 0;

        // Update state with tomorrow as start date
        state.start = tomorrow;

        // Update end date to maintain the same duration
        if (state.end) {
          state.end = addDays(duration, tomorrow);
        }
      }

      setFormState(state);
      // Reset confirmation flag for new session
      setHasShownUpdateDialogThisSession(false);
    };
    loadFormState();
  }, [])

  // New useEffect to mark initial load as complete AFTER state is first set
  useEffect(() => {
    if (formState !== null && isInitialLoad.current) {
      isInitialLoad.current = false;
    }
  }, [formState]); // Depends only on formState

  const _checkAdvancedMode = () => {
    if (userInitiatedAdvancedMode) {
      return true;
    }
    // We preserve advanced mode when state cannot be represented by simple aggregate controls.
    if (!formState) return false;

    for (const day of formState.days) {
      if (day.serviceTypes.length > 1) return true;
      if (day.serviceTypes.some(st => st.guestsCount !== day.totalGuests)) return true;
      if (day.overnightRoomsDZ > 0) return true;
      if (day.seatCountsManuallyEdited === true) return true; // Check if seat counts were manually edited
    }
    if ((formState.prevdayRoomsDZ || 0) > 0) return true;

    return false; // If none of the above, it's not advanced
  }

  useEffect(() => {
    if (formState) {
      onChange([formState.start, formState.end]);
    }
    setSimpleMode(!_checkAdvancedMode());
  }, [formState, userInitiatedAdvancedMode]);

  const [autoSubmitted, setAutoSubmitted] = React.useState(false);

  useEffect(() => {
    if (autoUpdate && formState && !autoSubmitted) {
      if (formState.placeName && formState.placeLat && formState.placeLng) {
        setAutoSubmitted(true);
        updateFormState(formState);
        // Auto-update integrations rely on this event to refresh host-side results without navigation.

        console.log('WIDGET: dispatching formState event', formState);

        window.dispatchEvent(
          new CustomEvent('onSeminargoWidgetStateChange', { detail: formState })
        );
      }
    }
  }, [autoUpdate, formState, autoSubmitted, platformUrl]);

  // Keep host listeners synchronized even when changes come from nested drawer/popup sections.
  useEffect(() => {
    if (formState) {
      window.dispatchEvent(
        new CustomEvent('onSeminargoWidgetStateChange', { detail: formState })
      );
    }
  }, [formState]);

  // Centralizing this keeps submit behavior consistent between manual and auto-update flows.
  const triggerUpdateAction = () => {
    if (!formState) return;
    localStorage.setItem('formStateManual', 'true'); // Mark as manual interaction
    localStorage.setItem('formState', JSON.stringify(formState)); // Persist changes
    // Dispatch the event that the main page listens to
    window.dispatchEvent(
      new CustomEvent('onSeminargoWidgetStateChange', { detail: formState })
    );
    if (autoUpdate && !autoSubmitted) { // If this was triggered by auto-update, mark as submitted
      setAutoSubmitted(true);
    }
  };

  // Edits to existing bookings are confirmed once per session to avoid accidental re-requests.
  const requestFormStateUpdate = (newState: IFormState) => {
    if (formState?.bookingRefCode && !hasShownUpdateDialogThisSession) {
      console.log('requestFormStateUpdate - setPendingFormState');
      // Editing and first change this session: Show dialog, store pending state
      setPendingFormState(newState);
      setShowUpdateConfirmDialog(true);
      setHasShownUpdateDialogThisSession(true);
    } else {
      // Not editing, or subsequent change: Apply directly
      console.log('requestFormStateUpdate - setFormState');
      setFormState(newState);
    }
  };

  // Drawer guest changes explicitly force advanced mode because they can diverge per-day values.
  const requestFormStateUpdateFromDrawerGuestSection = (newState: IFormState) => {
    setUserInitiatedAdvancedMode(true); // Mark that an advanced mode action occurred
    // Proceed with the existing logic of requestFormStateUpdate
    if (formState?.bookingRefCode && !hasShownUpdateDialogThisSession) {
      console.log('requestFormStateUpdateFromDrawerGuestSection - setPendingFormState');
      setPendingFormState(newState);
      setShowUpdateConfirmDialog(true);
      setHasShownUpdateDialogThisSession(true);
    } else {
      console.log('requestFormStateUpdateFromDrawerGuestSection - setFormState');
      setFormState(newState);
    }
  };

  const handleAddonAdded = () => {
    if (drawerScrollRef.current) {
      setTimeout(() => {
        drawerScrollRef.current?.scrollTo({
          top: drawerScrollRef.current.scrollHeight - 200,
          behavior: 'smooth'
        });
      }, 0);
    }
  };

  // Add state to control auto-focus
  const [shouldAutoFocus, setShouldAutoFocus] = useState(false);

  // Update the effect to set the auto-focus flag
  useEffect(() => {
    if (popupShow === 'place' || drawerShow === 'place') {
      setShouldAutoFocus(true);
    } else {
      setShouldAutoFocus(false);
    }
  }, [popupShow, drawerShow]);

  if (formState === null) {
    return <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: 20 }}><Spinner /></div>
  }

  const onCalChanged = (value: ValuePiece | [ValuePiece, ValuePiece]) => {
    if (Array.isArray(value) && value.length === 2) {
      const days = differenceInDays(value[1] || formState.end, value[0] || formState.start);
      let daysArray = [];
      // Find the maximum totalGuests from existing days
      const maxExistingGuests = formState.days.length > 0
        ? Math.max(...formState.days.map(day => day.totalGuests))
        : 0;

      if (days >= formState.days.length) {
        daysArray = [
          ...formState.days,
          ...Array.from({ length: days - formState.days.length + 1 }, (_, i) => ({
            day: i + formState.days.length,
            overnightRoomsEZ: 0,
            overnightRoomsDZ: 0,
            totalGuests: maxExistingGuests, // Use maxExistingGuests for new days
            occupancy: Array.from({ length: formState.meetingRooms }, (_, i) => {
              return {
                from: '08:00',
                to: '18:00',
              }
            }),
            seating: Array.from({ length: formState.meetingRooms }, (_, i) => 'UFORM'),
            seatCount: Array.from({ length: formState.meetingRooms }, (_, i) => 10),
            addons: [],
            serviceTypes: formState.days.length > 0 ? formState.days[0].serviceTypes.map(st => ({ ...st, guestsCount: maxExistingGuests })) : [], // Also update serviceTypes guestsCount
            seatCountsManuallyEdited: false,
            regularGuestsSynced: formState.days.length > 0 ? formState.days[0].regularGuestsSynced : true,
          })),
        ];
      } else {
        daysArray = formState.days.slice(0, days + 1);
      }
      requestFormStateUpdate({
        ...formState,
        start: (Array.isArray(value) && value[0]) || endOfDay(formState.start),
        end: (Array.isArray(value) && value[1]) || endOfDay(formState.start),
        days: daysArray,
      });
      onChange(value);
      if (!isHotelView) {
        setPopupShow('particiants');
      }
    }
  };

  return (
    <>
      {/* Render Confirmation Dialog */}
      <ConfirmationDialog
        open={showUpdateConfirmDialog}
        onClose={() => setShowUpdateConfirmDialog(false)}
        onCancel={() => {
          // Discard pending state and allow dialog again
          setPendingFormState(null);
          setHasShownUpdateDialogThisSession(false);
          setShowUpdateConfirmDialog(false);
        }}
        onConfirm={() => {
          // Apply pending state, trigger update, close dialog
          if (pendingFormState) {
            setFormState(pendingFormState);
            triggerUpdateAction();
            setPendingFormState(null);
          }
          setShowUpdateConfirmDialog(false);
        }}
        title={t('widget:confirmation.updateRequest')}
        description={t('widget:confirmation.updateDescription')}
      />

      {popupShow === 'place' && (
        <div
          style={{
            borderBottomRightRadius: '32px',
            borderTopLeftRadius: '32px',
            borderTopRightRadius: '32px',
            position: 'absolute',
            width: capsuleWrapperWidth,
            backgroundColor: 'white',
            height: (capsuleWrapperRef.current?.getBoundingClientRect().height || 0) + 'px',
          }}
        ></div>
      )}
      <div ref={capsuleWrapperRef} style={{ position: 'relative' }} onClick={() => {
        if (!widgetClickedOnce) {
          setWidgetClickedOnce(true);
        }
      }}>
        <>
          <BaseCapsule
            className="SeminargoCapsule"
            expanded={expanded}
            slimMode={!expanded}
            onClick={() => !expanded && setDrawerShow('')}
          >
            {!expanded ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <Icon name="meeting_room" /> {t('widget:buttons.planMeeting')}
              </div>
            ) : (
              <CapsuleTabContainer>

                {!isHotelView && <CapsuleTab
                  name="place"
                  label={t('widget:tabs.place')}
                  icon="place"
                  description={formState.placeName}
                  setPopupShow={(tabName) => {
                    setPopupShow(tabName);
                  }}
                  setDrawerShow={setDrawerShow}
                  simpleMode={simpleMode}
                  popupShow={popupShow}
                  expanded={popupExpanded}
                  capsuleLeftPosition={capsuleWrapperPositionLeft}
                  capsuleTopPosition={capsuleWrapperPositionTop}
                  capsuleWidth={capsuleWrapperWidth}
                  popupContent={<div style={{ padding: 20, width: 300 }}>
                    <GooglePlacesAutocomplete
                      options={{
                        types: ["(regions)"],
                      }}
                      style={{
                        backgroundColor: 'rgb(249, 250, 251)',
                        border: '1px solid #AC2A6E',
                        padding: '0.625rem',
                        borderRadius: '0.5rem',
                        width: '100%',
                        boxSizing: 'border-box',
                      }}
                      apiKey={'AIzaSyBQUQ-CRUNnxBECaV4OuoYcpC9eWTlNBr0'}
                      onPlaceSelected={(place) => {
                        localStorage.setItem('formStateManual', 'true');
                        const newFormState = {
                          ...formState,
                          placeName: place.formatted_address || place.name,
                          placeLat: typeof place.geometry.location.lat === 'function'
                            ? place.geometry.location.lat()
                            : place.geometry.location.lat,
                          placeLng: typeof place.geometry.location.lng === 'function'
                            ? place.geometry.location.lng()
                            : place.geometry.location.lng,
                          placeBounds: place.regionBounds || null,
                          searchedHotelId: place.hotelId || null
                        };
                        setFormState(newFormState);
                        window.dispatchEvent(
                          new CustomEvent('onSeminargoWidgetStateChange', {
                            detail: newFormState
                          })
                        );
                        setPopupShow('termin')
                      }}
                      defaultValue={formState.placeName || ''}
                      placeholder={t('widget:placeholder.placeOrHotel')}
                      className="google-places-autocomplete"
                      autoFocus={shouldAutoFocus && popupShow === 'place'}
                      language={i18n.language as 'de' | 'en'}
                      platformUrl={platformUrl}
                    />
                  </div>}
                />}
                <CapsuleTab
                  name="termin"
                  label={t('widget:tabs.date')}
                  icon="calendar"
                  description={
                    (formState.start && formState.end && format('dd.MM.', formState.start) + ' - ' + format('dd.MM.yy', formState.end)) || t('widget:placeholder.noDate')
                  }
                  popupShow={popupShow}
                  expanded={popupExpanded}
                  setPopupShow={(tabName) => {
                    setPopupShow(tabName);
                  }}
                  setDrawerShow={setDrawerShow}
                  simpleMode={simpleMode}
                  capsuleLeftPosition={capsuleWrapperPositionLeft}
                  capsuleTopPosition={capsuleWrapperPositionTop}
                  capsuleWidth={capsuleWrapperWidth}
                  popupContent={<CalendarSection
                    setPopupShow={(tabName) => {
                      setPopupShow(tabName);
                    }}
                    setDrawerShow={setDrawerShow}
                    availabilityChecker={availabilityChecker}
                    quickPriceCalculator={quickPriceCalculator}
                    vertical={false}
                    fullScreen={false}
                    drawer={false}
                    onCalChanged={onCalChanged}
                    calValue={calValue}
                  />}
                />
                <CapsuleTab
                  icon="people"
                  name="participants"
                  label={t('widget:tabs.participants')}
                  description={simpleMode ?
                    Math.max(...formState.days.map(v => v.totalGuests)).toString() :
                    ''
                  }
                  setPopupShow={(tabName) => {
                    setPopupShow(tabName);
                  }}
                  setDrawerShow={setDrawerShow}
                  simpleMode={simpleMode}
                  popupShow={popupShow}
                  expanded={popupExpanded}
                  capsuleLeftPosition={capsuleWrapperPositionLeft}
                  capsuleTopPosition={capsuleWrapperPositionTop}
                  capsuleWidth={capsuleWrapperWidth}
                  popupContent={<GuestSection
                    setPopupShow={(tabName) => {
                      setPopupShow(tabName);
                    }}
                    setDrawerShow={setDrawerShow}
                    setSimpleMode={setSimpleMode}
                    simpleMode={simpleMode}
                    requestFormStateUpdate={requestFormStateUpdate}
                    formState={formState}
                  />}
                />
                <CapsuleTab
                  icon="meeting_room"
                  name="meeting_rooms"
                  label={t('widget:tabs.rooms')}
                  tooltip={t('widget:tabs.tooltip.rooms')}
                  description={simpleMode ? formState.days.map(day => day.occupancy.length).reduce((a, b) => Math.max(a, b), 0).toString() : ''}
                  popupShow={popupShow}
                  setPopupShow={(tabName) => {
                    setPopupShow(tabName);
                  }}
                  setDrawerShow={setDrawerShow}
                  simpleMode={simpleMode}
                  expanded={popupExpanded}
                  capsuleLeftPosition={capsuleWrapperPositionLeft}
                  capsuleTopPosition={capsuleWrapperPositionTop}
                  capsuleWidth={capsuleWrapperWidth}
                  popupContent={
                    <MeetingRoomsSectionPopup
                      setSimpleMode={setSimpleMode}
                      simpleMode={true}
                      requestFormStateUpdate={requestFormStateUpdate}
                      formState={formState}
                      meetingRoomsDay={meetingRoomsDay}
                      setMeetingRoomsDay={setMeetingRoomsDay}
                      shadowRoot={shadowRoot}
                      availabilityChecker={availabilityChecker}
                      setDrawerShow={setDrawerShow}
                      setPopupShow={(tabName) => {
                        setPopupShow(tabName);
                      }}
                    />
                  }
                />
                <CapsuleTab
                  name="rooms"
                  label={t('widget:tabs.overnight')}
                  icon="doublebed"
                  description={simpleMode ? Math.max(
                    ...formState.days.map(v => v.overnightRoomsEZ),
                    formState.prevdayRoomsEZ,
                  ).toString() : ''
                  }
                  setPopupShow={(tabName) => {
                    setPopupShow(tabName);
                  }}
                  setDrawerShow={setDrawerShow}
                  simpleMode={simpleMode}
                  popupShow={popupShow}
                  expanded={popupExpanded}
                  capsuleLeftPosition={capsuleWrapperPositionLeft}
                  capsuleTopPosition={capsuleWrapperPositionTop}
                  capsuleWidth={capsuleWrapperWidth}
                  popupContent={<RoomsSection
                    setPopupShow={(tabName) => {
                      setPopupShow(tabName);
                    }}
                    setDrawerShow={setDrawerShow}
                    setSimpleMode={setSimpleMode}
                    simpleMode={simpleMode}
                    requestFormStateUpdate={requestFormStateUpdate}
                    formState={formState}
                  />}
                />

                <CapsuleTab
                  name="servicetypes"
                  label={t('widget:tabs.catering')}
                  icon="coffee"
                  description={simpleMode ? formState.days[0].serviceTypes.map(st =>
                    st.type
                      .replace('REGULAR', t('widget:serviceTypes.regular'))
                      .replace('SMALL', t('widget:serviceTypes.small'))
                      .replace('MEDIUM', t('widget:serviceTypes.medium'))
                  ).join(', ') : ''}
                  setPopupShow={(tabName) => {
                    setPopupShow(tabName);
                  }}
                  setDrawerShow={setDrawerShow}
                  simpleMode={simpleMode}
                  popupShow={popupShow}
                  expanded={popupExpanded}
                  capsuleLeftPosition={capsuleWrapperPositionLeft}
                  capsuleTopPosition={capsuleWrapperPositionTop}
                  capsuleWidth={capsuleWrapperWidth}
                  dynamicLeftOffset={500}
                  popupContent={<PackageSection
                    setPopupShow={(tabName) => {
                      setPopupShow(tabName);
                    }}
                    setDrawerShow={setDrawerShow}
                    meetingRoomsDay={meetingRoomsDay}
                    setMeetingRoomsDay={setMeetingRoomsDay}
                    advancedMode={false}
                    requestFormStateUpdate={requestFormStateUpdate}
                    formState={formState}
                  />}
                />

                {!isHotelView && (
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginRight: '20px',
                      marginLeft: '20px',
                    }}
                  >
                    <Button
                      color={'#AC2A6E'}
                      onClick={async () => {
                        setDrawerShow('');
                        setPopupShow(null);
                      }}
                      style={{
                        marginRight: '0px',
                        border: 0,
                        fontFamily: 'sans-serif',
                        fontSize: 13,
                        fontWeight: 'bold',
                        paddingLeft: 0,
                      }}
                    >
                      {t('widget:buttons.details')}
                    </Button>

                    {!isInitFormState && !formState?.bookingRefCode && (
                      <Button
                        color={'#AC2A6E'}
                        onClick={async () => {
                          localStorage.removeItem('formStateManual');
                          localStorage.removeItem('formState');
                          getInitialFormState().then((state) => {
                            setFormState({
                              ...state,
                              bookingRefCode: null
                            });
                            setUserInitiatedAdvancedMode(false); // Reset the flag
                            var event = new CustomEvent('formStateReset', {});
                            window.dispatchEvent(event);
                          });
                        }}
                        style={{
                          marginRight: '0px',
                          border: 0,
                          fontFamily: 'sans-serif',
                          fontSize: 13,
                          fontWeight: 'bold',
                          paddingLeft: 0,
                        }}
                      >
                        X
                      </Button>
                    )}
                    {!autoUpdate && (
                      <Button
                        color={'#AC2A6E'}
                        filled
                        iconPosition="left"
                        onClick={() => {
                          if (navDebug) {
                            console.log('[NAV_DEBUG] Search button clicked');
                            console.log('[NAV_DEBUG] platformUrl:', platformUrl);
                            console.log('[NAV_DEBUG] formState:', formState);
                          }
                          triggerUpdateAction();
                          if (platformUrl) {
                            const encodedState = encodeURIComponent(JSON.stringify(formState));
                            const targetUrl = `${platformUrl}?s=${encodedState}`;
                            if (navDebug) {
                              console.log('[NAV_DEBUG] Target URL:', targetUrl);
                              console.log('[NAV_DEBUG] Navigation blocked in debug mode');
                              return;
                            }
                            window.location.href = targetUrl;
                          } else if (navDebug) {
                            console.log('[NAV_DEBUG] platformUrl is empty, not navigating');
                          }
                        }}
                      >
                        {t('widget:buttons.search')}
                      </Button>
                    )}
                  </div>
                )}
              </CapsuleTabContainer>
            )}
          </BaseCapsule>
          <Drawer
            open={drawerShow !== null}
            onOverlayClick={() => setDrawerShow(null)}
            fullscreen={isMobileSize()}>

            <div
              style={{
                borderBottom: '1px solid #eef4fc',
                padding: '10px 20px',
              }}
            >
              <div
                style={{
                  cursor: 'pointer',
                  float: 'right',
                  fontFamily: 'Roboto, sans-serif',
                  marginRight: 10,
                  marginTop: 14,
                }}
                onClick={() => setDrawerShow(null)}
              >
                X
              </div>
              <h1 style={{ fontFamily: 'Roboto, sans-serif', fontSize: 16 }}>{t('widget:buttons.details')}</h1>
            </div>

            <div ref={drawerScrollRef} style={{ paddingBottom: '50px', maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
              <DrawerAccordion
                value={drawerShow}
                values={[

                  {
                    name: 'place',
                    label: t('widget:tabs.place'),
                    description: formState.placeName,
                    content: (
                      <div style={{ padding: 20 }}>
                        <GooglePlacesAutocomplete
                          options={{
                            types: ["(regions)"],
                          }}
                          style={{
                            backgroundColor: 'rgb(249, 250, 251)',
                            border: '1px solid #AC2A6E',
                            padding: '0.625rem',
                            borderRadius: '0.5rem',
                            width: '100%',
                            boxSizing: 'border-box',
                          }}
                          apiKey={'AIzaSyBQUQ-CRUNnxBECaV4OuoYcpC9eWTlNBr0'}
                          onPlaceSelected={(place) => {
                            localStorage.setItem('formStateManual', 'true');
                            const newFormState = {
                              ...formState,
                              placeName: place.formatted_address || place.name,
                              placeLat: typeof place.geometry.location.lat === 'function'
                                ? place.geometry.location.lat()
                                : place.geometry.location.lat,
                              placeLng: typeof place.geometry.location.lng === 'function'
                                ? place.geometry.location.lng()
                                : place.geometry.location.lng,
                              placeBounds: place.regionBounds || null,
                              searchedHotelId: place.hotelId || null
                            };
                            setFormState(newFormState);
                            window.dispatchEvent(
                              new CustomEvent('onSeminargoWidgetStateChange', {
                                detail: newFormState
                              })
                            );
                          }}
                          defaultValue={formState.placeName || ''}
                          placeholder={t('widget:placeholder.placeOrHotel')}
                          className="google-places-autocomplete"
                          autoFocus={shouldAutoFocus && drawerShow === 'place'}
                          language={i18n.language as 'de' | 'en'}
                          platformUrl={platformUrl}
                        />
                      </div>
                    ),
                  },
                  {
                    name: 'termin',
                    label: t('widget:tabs.date'),
                    description:
                      (formState.start && formState.end && format('dd.MM.', formState.start) + ' - ' + format('dd.MM.yy', formState.end)) || 'keine',
                    content: (
                      <>
                        <CalendarSection
                          quickPriceCalculator={quickPriceCalculator}
                          availabilityChecker={availabilityChecker}
                          fullScreen={isMobileSize()}
                          vertical={true}
                          drawer={true}
                          onCalChanged={onCalChanged}
                          calValue={calValue}
                          setDrawerShow={setDrawerShow}
                          setPopupShow={(tabName) => {
                            setPopupShow(tabName);
                          }}
                        />
                      </>
                    ),
                  },
                  {
                    name: 'participants',
                    label: t('widget:tabs.participants'),
                    description: simpleMode ?
                      Math.max(...formState.days.map(v => v.totalGuests)).toString() :
                      `${Math.min(...formState.days.map(v => v.totalGuests))} / ${Math.max(...formState.days.map(v => v.totalGuests))}`,
                    content: (
                      <GuestSection
                        setSimpleMode={setSimpleMode}
                        simpleMode={false}
                        requestFormStateUpdate={requestFormStateUpdateFromDrawerGuestSection}
                        formState={formState}
                        setDrawerShow={setDrawerShow}
                        setPopupShow={(tabName) => {
                          setPopupShow(tabName);
                        }}
                      />
                    )
                  },
                  {
                    name: 'meeting_rooms',
                    label: t('widget:tabs.rooms'),
                    description: (() => {
                      const dailyOccupancyCounts = formState.days.map(day => day.occupancy.length);
                      if (simpleMode) {
                        return dailyOccupancyCounts.reduce((max, current) => Math.max(max, current), 0).toString();
                      } else {
                        if (dailyOccupancyCounts.length > 0) {
                          const minOccupancy = Math.min(...dailyOccupancyCounts);
                          const maxOccupancy = Math.max(...dailyOccupancyCounts);
                          if (minOccupancy === maxOccupancy) {
                            return maxOccupancy.toString();
                          } else {
                            return `${minOccupancy} / ${maxOccupancy}`;
                          }
                        } else {
                          return '0';
                        }
                      }
                    })(),
                    content: (
                      <MeetingRoomsSectionDrawer
                        requestFormStateUpdateUserInitiated={requestFormStateUpdateFromDrawerGuestSection}
                        requestFormStateUpdateAutomatic={requestFormStateUpdate}
                        formState={formState}
                        meetingRoomsDay={meetingRoomsDay}
                        setMeetingRoomsDay={setMeetingRoomsDay}
                        shadowRoot={shadowRoot}
                        availabilityChecker={availabilityChecker}
                        setDrawerShow={setDrawerShow}
                        setPopupShow={(tabName) => {
                          setPopupShow(tabName);
                        }}
                      />
                    ),
                  },
                  {
                    name: 'rooms',
                    label: t('widget:tabs.overnight'),
                    description: simpleMode ? Math.max(
                      ...formState.days.map(v => v.overnightRoomsEZ),
                      formState.prevdayRoomsEZ,
                    ).toString() :
                      `${Math.min(...formState.days.map(v => v.overnightRoomsEZ), formState.prevdayRoomsEZ)} / ${Math.max(
                        ...formState.days.map(v => v.overnightRoomsEZ),
                        formState.prevdayRoomsEZ,
                      )}` || t('widget:placeholder.notSet'),
                    content: (
                      <RoomsSection
                        setSimpleMode={setSimpleMode}
                        setDrawerShow={setDrawerShow}
                        simpleMode={false}
                        requestFormStateUpdate={requestFormStateUpdate}
                        formState={formState}
                        setPopupShow={(tabName) => {
                          setPopupShow(tabName);
                        }}
                      />
                    )
                  },
                  {
                    name: 'servicetypes',
                    label: t('widget:tabs.catering'),
                    description: simpleMode ? formState.days[0].serviceTypes.map(st => st.type.replace('REGULAR', t('widget:serviceTypes.regular')).replace('SMALL', t('widget:serviceTypes.small')).replace('MEDIUM', t('widget:serviceTypes.medium'))).join(', ') : '',
                    content: (
                      <ServiceTypeSection
                        setDrawerShow={setDrawerShow}
                        requestFormStateUpdate={requestFormStateUpdate}
                        meetingRoomsDay={meetingRoomsDay}
                        setMeetingRoomsDay={setMeetingRoomsDay}
                        formState={formState}
                        advancedMode={true}
                        setPopupShow={(tabName) => {
                          setPopupShow(tabName);
                        }}
                        onAddonAdded={handleAddonAdded}
                      />
                    ),
                  },
                ]}
              />
            </div>
            <div
              style={{
                position: 'fixed',
                bottom: 0,
                right: drawerShow !== null ? 0 : (isMobileSize() ? '-100%' : '-550px'),
                width: isMobileSize() ? '100%' : '550px',
                textAlign: 'center',
                backgroundColor: 'white',
                fontSize: '12px',
                color: '#374151',
                padding: '20px',
                paddingBottom: '50px',
                borderTop: '1px solid #e5e7eb',
                boxSizing: 'border-box',
                zIndex: 10,
                transition: 'right 0.7s ease-in-out',
              }}
            >
              <Button
                fullWidth
                color="#AC2A6E"
                filled
                onClick={() => {
                  if (platformUrl) {
                    // Navigate to platform URL like the search button
                    triggerUpdateAction();
                    const encodedState = encodeURIComponent(JSON.stringify(formState));
                    const targetUrl = `${platformUrl}?s=${encodedState}`;
                    if (navDebug) {
                      console.log('[NAV_DEBUG] Continue button clicked');
                      console.log('[NAV_DEBUG] Target URL:', targetUrl);
                      return;
                    }
                    window.location.href = targetUrl;
                  } else {
                    if (!buttonEnabled) {
                      setDrawerShow(null);
                    } else {
                      // Original behavior for embedded widget
                      window.parent.postMessage({ type: 'checkUserAndNavigate' }, '*');
                      setDrawerShow(null);
                    }
                  }
                }}
                style={{
                  width: '100%',
                  maxWidth: '100%',
                }}
              >
                {t('widget:buttons.continue')} <Icon name="arrow-right" color="white" />
              </Button>
            </div>
          </Drawer>
          {popupShow !== null && <ClickOverlay open={true} onClick={() => setPopupShow(null)} />}
        </>
      </div>
    </>
  );
};

export default Capsule;
